import React, { useEffect, useState, useRef } from 'react'
import {
  Chip,
  Typography,
  Grid,
  TextField,
  Button,
  makeStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  IconButton,
  Divider,
  MenuItem,
} from '@material-ui/core'
import filesize from 'filesize'
import {
  ExpandMore as ExpandMoreIcon,
  Backup as UploadIcon,
  Cancel as CancelIcon,
  InsertDriveFile as AttachmentIcon,
} from '@material-ui/icons'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import history from '~/helpers/history'
import Portlet from '~/components/Portlet'
import { Creators as CircularsActions } from '~/store/ducks/circulars'
import Autocomplete from '@material-ui/lab/Autocomplete'
import RhfTextField from '~/components/FormComponents/RhfTextField'
import RhfSelect from '~/components/FormComponents/RhfSelect'
import OverlayLoading from '~/components/OverlayLoading'
import styles from './styles'

const loadingSelector = ({ circulars: { loading } }) => loading
const useStyles = makeStyles(styles)
const circularSelector = ({ circulars }) => circulars.circularData
const allDepartmentsSelector = ({ circulars: { allDepartments } }) => allDepartments

export default function CircularNewPage({ match }) {
  const { id } = match.params
  const fileInput = useRef(null)

  const isEdit = id !== 'new'
  const loading = useSelector(loadingSelector)
  const dispatch = useDispatch()
  const classes = useStyles()
  const circularData = useSelector(circularSelector)
  const { loading: allDepartmentsLoading, data: allDepartmentsData } = useSelector(allDepartmentsSelector)
  
  const [attachment, setAttachment] = useState('')

  const linkDownload = circularData?.url_pdf_file

  function handleUploadAttachment(e) {
    const file = e.target.files[0]
    if (file) {
      setAttachment(file)
    }
  }

  const { handleSubmit, control, reset, formState: { errors } } = useForm()
  
  useEffect(() => {
    if (isEdit) {
      reset(circularData)
    } else {
      reset({})
    }
    dispatch(CircularsActions.getAllDepartmentsRequest())
  }, [isEdit, circularData])

  useEffect(() => {
    if (isEdit) {
      dispatch(CircularsActions.getCircularRequest(id))
    }
    return () => {
      dispatch(CircularsActions.resetCircularData())
    }
  }, [isEdit, id])

  const handleFormCircularSubmit = (data) => {
    const formattedData = {
      ...data,
      active: data.active === 'true',
      department_ids: data.departments?.map((d) => d.id),
    }

    if (attachment) {
      formattedData.file = attachment
    }

    if (isEdit) {
      dispatch(
        CircularsActions.updateCircularRequest(id, formattedData, () =>
          history.push('/circulars'),
        ),
      )
    } else {
      dispatch(
        CircularsActions.addCircularRequest(formattedData, () => history.push('/circulars')),
      )
    }
  }

  return (
    <div className={classes.root}>
      <Portlet className={classes.grow} style={{ padding: 8 }}>
        <Grid container className={classes.container}>
          <div className={classes.tabsWrapper}>
            <div className={classes.root}>
              <div>
                {loading && <OverlayLoading />}
                {!loading && (
                  <form
                    onSubmit={handleSubmit(handleFormCircularSubmit)}
                    id="form-circular"
                  >
                    <Grid container spacing={2}>

                      <Grid item xs={12}>
                        <RhfTextField
                          label="Nome"
                          name="name"
                          control={control}
                          fullWidth
                          rules={{ required: true }}
                          error={!!errors.name}
                          helperText={!!errors.name && 'Campo obrigatório'}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <RhfSelect
                          name="status"
                          label="Status"
                          variant="outlined"
                          fullWidth
                          control={control}
                          defaultValue="active"
                        >
                          <MenuItem value="active">Ativo</MenuItem>
                          <MenuItem value="inactive">Inativo</MenuItem>
                        </RhfSelect>
                      </Grid>

                      <Grid item xs={12}>
                        <Controller
                          name="departments"
                          control={control}
                          defaultValue=""
                          rules={{ required: true }}
                          render={({ field: { onChange, value, ...rest } }) => (
                            <Autocomplete
                              fullWidth
                              // disabled={isView}
                              multiple
                              value={value || []}
                              onChange={(event, v) => {
                                onChange(v)
                              }}
                              disableCloseOnSelect
                              filterSelectedOptions
                              options={allDepartmentsData}
                              loading={allDepartmentsLoading}
                              {...rest}
                              getOptionLabel={(option) => option.name}
                              getOptionSelected={(option, val) => {
                                return option.id === val.id
                              }}
                              renderTags={(tagValue, getTagProps) =>
                                tagValue?.map((option, index) => (
                                  <Chip
                                    label={option.name}
                                    style={{
                                      backgroundColor: '#ED1E79',
                                      color: '#FFFFFF',
                                      // eslint-disable-next-line no-nested-ternary
                                    }}
                                    {...getTagProps({ index })}
                                  />
                                ))
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  error={!!errors.departments}
                                  helperText={
                                    !!errors.departments && 'Campo obrigatório'
                                  }
                                  label="Selecione os departamentos relacionados à esta circular*"
                                  variant="outlined"
                                />
                              )}
                            />
                          )}
                        />
                      </Grid>

  
                      <div className={classes.detailsWrapper}>
                        <Accordion defaultExpanded>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="caption">
                              Arquivo
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  onClick={() => {
                                    fileInput.current.click()
                                  }}
                                >
                                  <UploadIcon className={classes.uploadIcon} />
                                  Selecionar arquivo
                                  <input
                                    type="file"
                                    multiple={false}
                                    ref={fileInput}
                                    onClick={(e) => {
                                      e.target.value = ''
                                    }}
                                    style={{ display: 'none' }}
                                    onChange={handleUploadAttachment}
                                  />
                                </Button>
                              </Grid>
                              {attachment && (
                                <Grid item xs={12}>
                                  <Card className={classes.upload}>
                                    <CardContent
                                      className={classes.uploadContent}
                                    >
                                      <AttachmentIcon
                                        className={classes.uploadIcon}
                                      />
                                      <div className={classes.uploadDetail}>
                                        <span>{attachment.name}</span>
                                        <span className={classes.fileSize}>
                                          {filesize(attachment.size, {
                                            round: 0,
                                          })}
                                        </span>
                                      </div>
                                      <IconButton
                                        onClick={() => setAttachment('')}
                                      >
                                        <CancelIcon
                                          className={classes.cancelUploadIcon}
                                        />
                                      </IconButton>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              )}
                              {!attachment && !!linkDownload && (
                                <Button
                                  className={classes.buttonDonwload}
                                  // color="primary"
                                  // variant="outlined"
                                >
                                  <a
                                    className={classes.link}
                                    id="anchorId"
                                    href={linkDownload}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Baixar Arquivo
                                  </a>
                                </Button>
                              )}
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      </div>
                    </Grid>
                  </form>
                )}
              </div>
              <Grid
                container
                className={classes.buttonForm}
                justifyContent="flex-end"
              >
                <Button
                  color="primary"
                  type="submit"
                  form="form-circular"
                  variant="contained"
                >
                  SALVAR
                </Button>
              </Grid>
            </div>
          </div>
        </Grid>
        <Divider />

        <Grid
          container
          justifyContent="space-between"
          className={classes.actionsWrapper}
        >
          <Grid item>
            <Button variant="contained" onClick={() => history.push('/circulars')}>
              Voltar
            </Button>
          </Grid>
        </Grid>
      </Portlet>
    </div>
  )
}

CircularNewPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
}
